import { useCallback, useEffect, useState } from "react";

import { Input } from "antd";

const MAX_RADIUS_METERS = 10000;

interface RadiusInputProps {
  label: string;
  displayLabel: string;
  metersValue: number;
  disabled: boolean;
  metersToDisplay: (meters: number) => number;
  displayToMeters: (display: number) => number;
  isReady: boolean;
  onChange: (metersValue: number) => void;
}

export default function RadiusInput({
  label,
  displayLabel,
  metersValue,
  disabled,
  metersToDisplay,
  displayToMeters,
  isReady,
  onChange
}: RadiusInputProps) {
  const [displayedValue, setDisplayedValue] = useState<string>(
    metersToDisplay(metersValue).toString()
  );
  const [isEditing, setIsEditing] = useState(false);
  const maxDisplayValue = metersToDisplay(MAX_RADIUS_METERS);

  useEffect(() => {
    if (isReady && !isEditing) {
      const convertedValue = metersToDisplay(metersValue);
      const roundedValue = Math.round(convertedValue * 100) / 100;
      setDisplayedValue(roundedValue.toString());
    }
  }, [metersValue, isReady, metersToDisplay, isEditing]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    const numericValue = parseFloat(displayedValue);

    if (!isNaN(numericValue) && numericValue >= 0) {
      const clampedValue = Math.min(numericValue, maxDisplayValue);
      const roundedValue = Math.round(clampedValue * 100) / 100;
      setDisplayedValue(roundedValue.toString());
      onChange(displayToMeters(roundedValue));
    } else {
      const convertedValue = metersToDisplay(metersValue);
      const roundedValue = Math.round(convertedValue * 100) / 100;
      setDisplayedValue(roundedValue.toString());
    }
  }, [
    displayedValue,
    displayToMeters,
    metersToDisplay,
    metersValue,
    onChange,
    maxDisplayValue
  ]);

  return (
    <div className="flex-column" style={{ gap: "3px" }}>
      <span>
        {label} ({displayLabel})
      </span>
      <Input
        type="number"
        step={1}
        min={0}
        max={maxDisplayValue}
        disabled={disabled}
        value={displayedValue}
        onFocus={() => setIsEditing(true)}
        onChange={(e) => setDisplayedValue(e.target.value)}
        onBlur={handleBlur}
      />
    </div>
  );
}
